import React from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { FaShieldHalved, FaLock, FaUserShield, FaEnvelope, FaCookieBite } from 'react-icons/fa6';
import Seo from '@/components/seo';

export default function PrivacyPage() {
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://aboutcar.com.br/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Política de Privacidade',
        item: 'https://aboutcar.com.br/privacidade'
      }
    ]
  };

  return (
    <>
      <Seo
        title="Política de Privacidade | AboutCar"
        description="Conheça a Política de Privacidade da AboutCar. Saiba como coletamos, utilizamos, protegemos e gerenciamos seus dados pessoais em conformidade com a LGPD."
        keywords="politica de privacidade, lgpd aboutcar, protecao de dados, dados pessoais consulta veicular, termos privacidade"
        schema={breadcrumbSchema}
      />

      {/* Hero Section */}
      <section className="bg-light-subtle border-bottom py-5">
        <Container className="py-2">
          <Row className="align-items-center g-4">
            <Col lg={8}>
              <span className="badge bg-primary text-white rounded-pill px-3 py-1 text-uppercase fw-bold mb-3 fs-7">
                LGPD & Transparência
              </span>
              <h1 className="display-4 fw-extrabold text-dark mb-3">
                Política de <span className="text-primary">Privacidade</span>
              </h1>
              <p className="lead text-secondary mb-4">
                Saiba como a AboutCar coleta, utiliza, protege e gerencia seus dados pessoais em total conformidade com a Lei Geral de Proteção de Dados (LGPD).
              </p>

              <div className="d-flex flex-wrap gap-3">
                <div className="d-flex align-items-center gap-2 bg-white px-3 py-2 rounded-pill shadow-sm border">
                  <FaShieldHalved className="text-success" />
                  <span className="small fw-semibold text-dark">Conforme LGPD (Lei nº 13.709/18)</span>
                </div>
                <div className="d-flex align-items-center gap-2 bg-white px-3 py-2 rounded-pill shadow-sm border">
                  <FaLock className="text-primary" />
                  <span className="small fw-semibold text-dark">Dados 100% Criptografados</span>
                </div>
              </div>
            </Col>

            <Col lg={4} className="text-center text-lg-end">
              <div className="p-4 bg-white rounded-4 shadow-sm border border-light-subtle text-start d-inline-block w-100 max-w-350">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="bg-primary-subtle text-primary p-3 rounded-circle">
                    <FaUserShield className="fs-3" />
                  </div>
                  <div>
                    <h6 className="fw-bold text-dark mb-0">Canal do DPO</h6>
                    <span className="small text-muted">Data Protection Officer</span>
                  </div>
                </div>
                <p className="small text-secondary mb-3">Dúvidas sobre seus dados? Fale diretamente com o nosso encarregado de privacidade.</p>
                <a href="mailto:dpo@aboutcar.com.br" className="btn btn-outline-primary rounded-pill btn-sm fw-bold w-100 d-flex align-items-center justify-content-center gap-2">
                  <FaEnvelope /> dpo@aboutcar.com.br
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Content Section */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            <Card className="border-0 shadow-sm rounded-4 p-4 p-md-5 mb-4">
              <div className="d-flex flex-column gap-4 text-secondary leading-relaxed">

                {/* 1. Introdução */}
                <section id="section_1">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>01</span>
                    Política de Privacidade
                  </h3>
                  <p className="ms-md-4">
                    A <strong>ABOUTCAR SOLUÇÕES</strong> (&quot;AboutCar&quot;) leva a privacidade muito a sério. Por isso, neste termo de política de Privacidade (“Política”) explicamos as formas como nós utilizamos, armazenamos, compartilhamos e protegemos os dados pessoais coletados a partir do uso dos nossos serviços e soluções, além da visita aos nossos websites.
                  </p>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 2. Definições */}
                <section id="section_2">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>02</span>
                    Definições Importantes
                  </h3>
                  <div className="ms-md-4 d-flex flex-column gap-3">
                    <div>
                      <strong className="text-dark d-block mb-1">Usuário:</strong>
                      <span>Todas as pessoas físicas que utilizarão ou visitarão o(s) Site(s) e/ou plataformas digitais, maiores de 18 (dezoito) anos ou emancipadas.</span>
                    </div>

                    <div>
                      <strong className="text-dark d-block mb-1">Dados Pessoais:</strong>
                      <span>Quaisquer informações fornecidas e/ou coletadas pela AboutCar e/ou seus parceiros comerciais, por qualquer meio, ainda que públicos, que identifiquem ou possam identificar um indivíduo.</span>
                    </div>

                    <div>
                      <strong className="text-dark d-block mb-1">Finalidade:</strong>
                      <span>O objetivo e o propósito que a AboutCar deseja alcançar a partir de cada ato de tratamento das informações pessoais.</span>
                    </div>

                    <div>
                      <strong className="text-dark d-block mb-1">Bases Legais:</strong>
                      <span>Instrumento ou justificativa legal que torna legítimo o tratamento de dados pessoais para uma determinada finalidade.</span>
                    </div>

                    <div>
                      <strong className="text-dark d-block mb-1">Consentimento:</strong>
                      <span>Autorização expressa e inequívoca dada pelo Usuário titular do dado pessoal para que a AboutCar trate seus dados pessoais para uma finalidade previamente descrita.</span>
                    </div>
                  </div>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 3. Coleta de Dados */}
                <section id="section_3">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>03</span>
                    Como Coletamos Dados Pessoais?
                  </h3>
                  <p className="ms-md-4 mb-3">
                    O Usuário está ciente de que fornece informação de forma consciente e voluntária por meio de formulários digitais ou navegando nos sites operados pela AboutCar.
                  </p>
                  <ul className="ms-md-4 d-flex flex-column gap-2 mb-0">
                    <li>Todas as informações confiadas no cadastro serão para uso do sistema AboutCar, sendo incorporadas ao banco de dados da empresa;</li>
                    <li>Os dados dos usuários são coletados para que seja possível que a AboutCar entregue seus Serviços, processe pagamentos e direcione ofertas de interesse comum;</li>
                    <li>Coletamos dados para cumprir obrigações legais, regulatórias, judiciais e administrativas.</li>
                  </ul>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 4. Uso dos Dados */}
                <section id="section_4">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>04</span>
                    Como Usamos os Seus Dados?
                  </h3>
                  <p className="ms-md-4 mb-3">
                    Quando o Usuário realiza o cadastro e/ou preenche formulários oferecidos pela AboutCar, os Dados Pessoais solicitados serão mantidos em sigilo e utilizados exclusivamente para os propósitos informados.
                  </p>
                  <ul className="ms-md-4 d-flex flex-column gap-2 mb-0">
                    <li>Entrega de serviços contratados e suporte ao cliente;</li>
                    <li>Respostas a contatos, dúvidas e envio de comunicados operacionais importantes;</li>
                    <li>Prevenção a fraudes, segurança cibernética e integridade dos sistemas.</li>
                  </ul>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 5. Proteção de Dados */}
                <section id="section_5">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>05</span>
                    Como Protegemos Seus Dados?
                  </h3>
                  <p className="ms-md-4 mb-3">
                    A AboutCar e seus fornecedores utilizam padrões rígidos de segurança de informação para proteger a confidencialidade, segurança e integridade de seus Dados Pessoais.
                  </p>
                  <ul className="ms-md-4 d-flex flex-column gap-2 mb-0">
                    <li>Medidas físicas e lógicas de proteção de ativos, controle rigoroso de acessos e protocolos de criptografia;</li>
                    <li>Armazenamento seguro em nuvem de alta confiabilidade em conformidade com as exigências da LGPD.</li>
                  </ul>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 6. Cookies */}
                <section id="section_6">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '32px', height: '32px', fontSize: '0.85rem' }}>06</span>
                    Cookies e Tecnologias de Rastreamento
                  </h3>
                  <p className="ms-md-4 mb-3">
                    Cookies são pequenos arquivos contendo dados que um site envia para o navegador do seu dispositivo para otimizar sua navegação e personalizar recursos.
                  </p>
                  <div className="ms-md-4 bg-light p-3 rounded-3 border border-light-subtle d-flex align-items-center gap-3">
                    <FaCookieBite className="fs-2 text-primary flex-shrink-0" />
                    <div>
                      <span className="d-block small text-muted">Quer saber mais sobre as categorias de cookies que utilizamos?</span>
                      <a href="/cookies" className="fw-bold text-primary text-decoration-none">
                        Consulte nossa Política de Cookies dedicada &rarr;
                      </a>
                    </div>
                  </div>
                </section>

                <hr className="my-2 border-light-subtle" />

                {/* 7. Revisões e Contato */}
                <section id="section_7">
                  <h3 className="fw-bold text-dark fs-5 mb-3 d-flex align-items-center gap-2">
                    <span className="badge bg-primary-subtle text-primary rounded-circle p-2 fs-7">07</span>
                    Revisões e Contato do DPO
                  </h3>
                  <p className="ms-md-4 mb-3">
                    Este documento é válido a partir de 01/02/2021. Reservamo-nos o direito de atualizar este termo conforme exigências legais ou aprimoramento de nossos serviços.
                  </p>
                  <div className="ms-md-4 bg-primary-subtle text-dark p-4 rounded-4 border border-primary-subtle">
                    <h6 className="fw-bold text-primary mb-1">Encarregado pelo Tratamento de Dados (DPO)</h6>
                    <p className="small mb-2 text-secondary">Prestador de Serviços: Geraldo Matos</p>
                    <p className="small mb-0">
                      Para exercer seus direitos de titular de dados ou esclarecer dúvidas sobre esta Política, envie um e-mail para: <strong className="text-primary">dpo@aboutcar.com.br</strong>.
                    </p>
                  </div>
                </section>

              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
